/**
 * Naming the game globals.
 *
 * A GVAR is an index into one flat int32 array. The engine never stores the
 * names: `scriptsSaveGameGlobalVars` writes `gGameGlobalVars` and nothing else,
 * and `gGameGlobalVarsLength` comes from counting declarations in
 * `data/vault13.gam` at startup (engine/src/scripts.cc, `_scr_game_init` ->
 * `_db_get_num_globals` path). So the only authoritative name table lives in the
 * player's own game data, not in this repo.
 *
 * That shapes the design:
 *
 *   - `parseGamGlobals` reads the player's `vault13.gam` and returns the real,
 *     complete index -> name map. This is the path the UI should prefer: the
 *     names then match whatever data the player actually installed, mods and all.
 *   - `WELL_KNOWN_GVARS` is a deliberately small fallback for when the game data
 *     is not to hand. Every entry carries the source that established it. We do
 *     not ship a bulk transcription of a game data file, and we do not guess.
 *   - Anything unnamed stays numeric. An index with no name is displayed as
 *     `GVAR 412`, never as a plausible-looking invention.
 *
 * Format of `vault13.gam`, and why declaration order is the index:
 *
 *     GAME_GLOBAL_VARS:
 *     //GLOBAL                                  NUMBER
 *
 *     GVAR_PLAYER_REPUTATION                  :=0;    //      (0)
 *     GVAR_TOWN_REP_ARROYO                    :=50;   //      (47)
 *
 * Scripts address globals by number, and the number is the position of the
 * declaration. The trailing `// (N)` comment looks authoritative and is not — it
 * is hand-maintained and wrong in places in the stock file (two duplicated
 * numbers put a run of ~28 lines one behind before a skipped number puts them
 * back in step). We parse it anyway and report disagreements as a cross-check,
 * but declaration order is what we index by.
 *
 * The parser is written against the stock file's actual shape, not a tidy
 * version of it: CRLF line endings, a last line with no terminator, an optional
 * trailing `;`, non-zero initial values, sometimes-absent `// (N)` comments, and
 * a commented-out `//GAME_GLOBAL_VARS:` header line above the live one.
 */

export type GamVar = {
  /** Declaration order. This is the GVAR index scripts and saves use. */
  index: number;
  /** e.g. `GVAR_START_ARROYO_TRIAL`. */
  name: string;
  /** Initial value from the declaration. Zero when the value did not parse. */
  value: number;
  /** True when the value parsed as a decimal integer. */
  valueParsed: boolean;
  /** The `// (N)` annotation, when present. Advisory only. */
  commentIndex: number | null;
};

export type GamGlobals = {
  vars: GamVar[];
  /** Indices where the `// (N)` comment disagrees with declaration order. */
  commentDisagreements: number[];
};

const DECLARATION = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*:=\s*(-?\d+)?\s*;?\s*(?:\/\/\s*\(?\s*(\d+)\s*\)?)?\s*$/;

/**
 * Parse a `.gam` variable-declaration file. Cannot throw: a line that does not
 * look like a declaration is skipped, and a value that does not parse leaves the
 * declaration in place with `valueParsed: false`. A malformed file yields fewer
 * names, never a wrong index.
 */
export function parseGamGlobals(text: string): GamGlobals {
  const vars: GamVar[] = [];
  const commentDisagreements: number[] = [];
  let inSection = false;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();
    if (trimmed.startsWith('//')) continue; // includes the commented-out header
    if (/^[A-Z_]+_VARS\s*:\s*$/.test(trimmed)) {
      inSection = true;
      continue;
    }
    if (!inSection) continue;

    const m = DECLARATION.exec(line);
    if (!m) continue;

    const name = m[1]!;
    const valueText = m[2];
    const value = valueText === undefined ? 0 : Number.parseInt(valueText, 10);
    const valueParsed = valueText !== undefined && Number.isFinite(value);
    const commentIndex = m[3] === undefined ? null : Number.parseInt(m[3], 10);

    const index = vars.length;
    if (commentIndex !== null && commentIndex !== index) commentDisagreements.push(index);
    vars.push({ index, name, value: valueParsed ? value : 0, valueParsed, commentIndex });
  }

  return { vars, commentDisagreements };
}

/** index -> name, from a parsed `.gam`. */
export function gamNameTable(globals: GamGlobals): Map<number, string> {
  return new Map(globals.vars.map((v) => [v.index, v.name]));
}

/**
 * The handful of globals we can name without the player's game data.
 *
 * Each entry states where the fact came from. This table is intentionally tiny:
 * a long list assembled from memory would be worse than no list, because a
 * confidently mislabelled quest flag is how a save editor gets someone to break
 * their own playthrough.
 */
export const WELL_KNOWN_GVARS: ReadonlyMap<number, string> = new Map([
  // Source: the first declaration in stock `data/vault13.gam`.
  [0, 'GVAR_PLAYER_REPUTATION'],
  // Source: decoded from a lawfully owned master.dat by disassembling the Arroyo
  // scripts (acklint.int, actemvil.int, arvillag.int), cross-checked against the
  // name table in data/vault13.gam. Master flag for the Temple of Trials:
  //   0 = not started, 1 = mid-trial, 2 = passed Cameron, 3 = done and rewarded.
  [10, 'GVAR_START_ARROYO_TRIAL'],
]);

/**
 * Build the naming function the UI should use: the player's own `vault13.gam`
 * when available, the small verified table otherwise, and a numeric label when
 * neither knows the index.
 */
export function makeGvarNamer(
  globals?: GamGlobals,
  /**
   * Which game's globals these are. Both entries in `WELL_KNOWN_GVARS` were
   * established from Fallout 2's `vault13.gam` and its Arroyo scripts, and
   * Fallout 1 has its own file with its own declaration order — so on a Fallout
   * 1 save the fallback table is skipped entirely and unnamed indices stay
   * numeric. A confidently mislabelled quest flag is the exact failure this
   * module exists to avoid; being wrong about the *other game's* flag is no
   * better than being wrong about this one's.
   *
   * The player's own `data/vault13.gam` still supplies real names when the
   * install has one, which Fallout 1 does.
   */
  game: 'fo1' | 'fo2' = 'fo2',
): (index: number) => string {
  const fromGam = globals ? gamNameTable(globals) : null;
  const fallback = game === 'fo1' ? null : WELL_KNOWN_GVARS;
  return (index) => fromGam?.get(index) ?? fallback?.get(index) ?? `GVAR ${index}`;
}

/**
 * Look an index up by name, using the player's `.gam` if supplied. Returns -1
 * when the name is unknown — callers should treat that as "type the number".
 */
export function gvarIndexByName(name: string, globals?: GamGlobals): number {
  const wanted = name.toUpperCase();
  if (globals) {
    for (const v of globals.vars) {
      if (v.name.toUpperCase() === wanted) return v.index;
    }
  }
  for (const [index, known] of WELL_KNOWN_GVARS) {
    if (known.toUpperCase() === wanted) return index;
  }
  return -1;
}

/**
 * Stock Fallout 2 declares this many globals. Useful as a `gvarCount` hint when
 * a file's two GVAR blocks disagree and auto-detection cannot run.
 *
 * Established two ways that agree: counting declarations in a lawfully owned
 * `data/vault13.gam` (695, indices 0..694), and the codec's own recovery of N
 * from a real SAVE.DAT written by this engine.
 */
export const STOCK_GVAR_COUNT = 695;
